import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/**
 * A short explanation attached to something on screen.
 *
 * Not a `title` attribute: those never appear on a touch screen, cannot be read
 * by most screen readers reliably, and take a second to show. This opens on
 * hover for a mouse AND on tap or keyboard for everyone else, which is the only
 * way an explanation is available to the person who most needs it.
 *
 * Wrap whatever it explains:
 *   <Info what="Why this is approximate">…the thing…</Info>
 */
export default function Info({
  title,
  children,
  what,
  className = '',
}: {
  /** The heading of the explanation. */
  title: string;
  /** What is being explained — rendered inline, unchanged. */
  children: React.ReactNode;
  /** The explanation itself. */
  what: React.ReactNode;
  /** Layout class for the wrapper — it, not the child, is the flex item now. */
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    // Claim Escape in the CAPTURE phase and stop it, so the keypress that closes the tip
    // does NOT also reach a parent overlay's own Escape-to-close (a CPI/SPI tip inside the
    // status drill was dumping the whole drill — the L25/L37 / portaled-menu-escape-capture
    // class). Capture runs before the parent's bubble handler, so the tip consumes it first.
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc, true);
    };
  }, [open]);

  // Fit the card to the viewport (NEW-2): it opens to the RIGHT of the dot by default, which
  // runs a rightmost tip (e.g. the SPI index) off the right edge with no scroll. Measure once
  // on open and flip it to open LEFT when its right edge would overflow. useLayoutEffect so the
  // flip lands before paint — no flash. Desktop-only; a 300px card always fits one side.
  const card = useRef<HTMLSpanElement>(null);
  const [flip, setFlip] = useState(false);
  useLayoutEffect(() => {
    if (!open) { setFlip(false); return; }
    const el = card.current;
    if (!el) return;
    setFlip(el.getBoundingClientRect().right > window.innerWidth - 8);
  }, [open]);

  return (
    <span
      className={`info-wrap ${className}`}
      ref={wrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {/* The dot and its card are wrapped together so the card hangs from the
          DOT, not the whole row — otherwise it anchors to the left edge of the
          (often wide) thing being explained and floats off to the side. R9. */}
      <span className="info-anchor">
        <button
          type="button"
          className="info-dot"
          aria-label={`What does this mean? ${title}`}
          aria-expanded={open}
          aria-describedby={open ? id : undefined}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          i
        </button>
        {open && (
          <span className={`info-card${flip ? ' flip' : ''}`} ref={card} role="note" id={id}>
            <strong className="info-card-title">{title}</strong>
            <span className="info-card-body">{what}</span>
          </span>
        )}
      </span>
    </span>
  );
}
